// The light theme's values. Components use `colors` (below), never these: `colors` wraps each value in a
// CSS variable, so one reference follows the active theme. The dark values of every variable are in
// src/theme/jungleThemeColors.css. Import the raw values only where a CSS variable cannot be read (a canvas,
// a meta tag, a value handed to the native app); see src/theme/resolveThemeColor.ts.
const lightThemeColorValues = {
  brand: {
    // The Jungle wordmark
    wordmark: "#005306",
  },
  voiceCallControls: {
    // The mute button while the microphone is on, and while it is muted (lime on the light theme)
    unmutedBg: "#FFFFFF",
    mutedBg: "#E4FFD1",
    unmutedHoverBg: "#F4F8F5",
    unmutedShadow: "0 2px 10px rgba(40, 60, 50, 0.10)",
    unmutedHoverShadow: "0 6px 18px rgba(40, 60, 50, 0.14)",
    mutedHoverBg: "#D5F5BA",
    mutedShadow: "0 6px 18px rgba(120, 160, 60, 0.22)",
    mutedHoverShadow: "0 8px 24px rgba(120, 160, 60, 0.32)",
    endBg: "#FF3B30",
    endHoverBg: "#E0312A",
    endShadow: "0 4px 14px rgba(0, 0, 0, 0.13)",
  },
  surface: {
    white: "#FFFFFF",
    offWhite: "#FCFFFD",
    offGreen: "#F9FFF7",
    primary: "#F5FCF8",
    secondary: "#EDF7F1",
    border: "#E1EBE5",
    tertiary: "#D8E0DC",
    questions: "#E4FFD1",
    multipleChoiceQuestions: "#E4FFD1",
    borderSecondary: "#D4EDC2",
    hoverBorder: "#ADC29F",
    background: "#F5F7F6",
    hover: "#EAEDEB",
    focus: "#E1EBE5",
    borderQuestion: "#D2EBC0",
    borderMultipleChoiceQuestion: "#D2EBC0",
    borderTrueOrFalseQuestion: "#CDE5BB",
    gradient: "#C8FFCD",
    progressBackground: "#EBF4ED",
    progress: "#8EF4A9",
    lightGreen: "rgba(243, 252, 247, 0.88)",
    // The mint-to-lemon frame around a confirmation dialog
    frameGradient: "linear-gradient(#C8FFCD, #FAFFD4)",
    // A dark surface that carries white text in both themes (a tooltip, a floating label)
    inverse: "#4C4D4C",
    // The dimmed layer behind a modal or over a page that is covered
    overlay: "rgba(0, 0, 0, 0.48)",
    // A see-through version of the page surface, for a header that lets content show underneath
    translucent: "rgba(255, 255, 255, 0.8)",
    // The halo around the progress bar (a tight core and a wide outer layer) and the fill of the part
    // that was just earned. Lime on the light theme's pale ground; the dark theme uses the brand's mint,
    // because lime turns olive on a dark ground.
    progressGlowInner: "hsl(80, 100%, 72%)",
    progressGlowOuter: "hsl(80, 100%, 55%)",
    progressGain: "linear-gradient(180deg, #C8FF7C 0%, #9CF45C 55%, #74E63E 100%)",
    // The see-through wash over a selected block in the notes editor. The editor's own blue on the light
    // theme; the brand's green on a dark one, where that blue would be the only blue on the page.
    noteSelection: "rgba(35, 131, 226, 0.14)",
    // The rows of a class's own sets, opened under it in a list. One step from the card, and still not
    // the color of the rail of radio buttons beside them.
    nestedRows: "#F8FAF9",
    // The body of a field that sits on a card, a panel or a dialog (src/theme/fieldWell.ts). Each field
    // keeps its own light fill, so this light value is only the fallback of a field that names none.
    fieldWell: "#FFFFFF",
    // A raised pill to choose (an answer in onboarding, a way to sign up): its fill at rest, under the
    // pointer and when chosen, and its line at rest and when chosen. White on a white sheet with a soft
    // shadow on the light theme; see RAISED_PILL_FILL below for what a dark sheet needs. A component whose
    // own light value differs keeps it with lightColorWithDarkTheme.
    // Chosen is green in both themes. It used to be the grey of a pill under the pointer on the light
    // theme, so the answer someone had picked looked the same as one they were passing over, or disabled.
    raisedPill: "#FFFFFF",
    raisedPillUnderPointer: "#EEF0EF",
    raisedPillChosen: "#DCFAE8",
    raisedPillLine: "#E4E5E5",
    raisedPillChosenLine: "#258548",
  },
  primaryAction: {
    light: "#F5FFF9",
    light2: "#DCFAE8",
    selectedLight: "#C5EAD3",
    selectedLightHover: "#B9E2C9",
    selectedLightBorder: "#8DBCA0",
    // Design system --action-primary-200: muted green for disabled composer send, etc.
    inactiveButton: "#A6C9B3",
    inactive: "rgba(22, 163, 74, 0.44)",
    generateButton: "#16A34A",
    default: "#258548",
    hover: "#005720",
    pressed: "#003D16",
    // default / hover / pressed double as text and border colors, which get lighter in the dark theme.
    // A solid button that carries white text uses these instead: they stay dark enough for the label.
    fill: "#258548",
    fillHover: "#005720",
    fillPressed: "#003D16",
    // The line of an outline button at rest. The same green as its label in the light theme; calmer than
    // the label in the dark theme, so an outline button never outshines the solid one beside it.
    outlineBorder: "#258548",
    // A solid button that cannot be pressed yet, and its label. In the dark theme the label is muted too:
    // a white label on a dark green fill would read as a button that works.
    disabledFill: "rgba(22, 163, 74, 0.44)",
    disabledLabel: "#FFFFFF",
    // The mint-to-lime wash behind a hovered or busy sign-in button
    highlightGradient:
      "linear-gradient(91.15deg, rgba(187, 255, 193, 0.8) 2.15%, rgba(215, 255, 190, 0.778646) 48.72%, rgba(249, 255, 186, 0.6) 98.3%)",
    // The color under the moving rays of the plan screens' main button, at rest and under the pointer. The
    // rays darken it, so on a dark sheet it starts brighter: the button that takes the money must not be
    // the dullest green on the screen.
    heroFill: "#1AAC50",
    heroFillHover: "#258548",
    // The darker half of a split button (the arrow beside "start studying"), at rest and under the pointer
    splitFill: "#076529",
    splitFillHover: "#055221",
    // Small green words on a green-tinted panel (a menu's options, a line of small print on a tinted
    // card). On the light theme's pale tints it is a shade darker than the brand green, which is 4.2:1 on
    // the mint of a soft button (this is 4.8:1). On a dark tint that green is too close to its ground for
    // small text (the owner's note on the study-mode menu, 2026-10-02), so there it is the lightest green
    // of the family.
    textOnTint: "#1E7A40",
    // The fill of "generate new questions" in the navigation. The soft mint of a secondary button on the
    // light theme; on a dark sidebar that tint was the selected page's pill again, so there it is greener.
    navigationButtonFill: "#DCFAE8",
  },
  secondaryAction: {
    light: "#EEF0EF",
    light2: "#e6e6e6", // modified pressed state for ghost buttons
    inactive: "#C9CBCA",
    inactiveDarker: "#979998",
    default: "#6A6B6A",
    hover: "#4C4D4C",
    pressed: "#2D2E2D",
    // Solid grey buttons with white text (see primaryAction.fill)
    fill: "#6A6B6A",
    fillHover: "#4C4D4C",
    fillPressed: "#2D2E2D",
    // A solid grey button that cannot be pressed yet (see primaryAction.disabledFill)
    disabledFill: "#C9CBCA",
    disabledLabel: "#FFFFFF",
    // The back and skip keys under a question: the key's face, its outline, and the hard shadow that
    // makes it look raised. The shadow is darker than the face in both themes.
    // The line of a grey outline button or chip at rest. The same grey as its label on the light theme;
    // calmer than the label on a dark one (see primaryAction.outlineBorder).
    outlineBorder: "#6A6B6A",
    keycapFace: "#F5FFF9",
    keycapBorder: "#6A6B6A",
    keycapShadow: "#6A6B6A",
  },
  tertiaryAction: {
    hover: "#EAB308",
    // Amber words on white and the amber fill under a white label: 4.9:1 either way (it was #CA8A04, 2.9:1)
    pressed: "#A16207",
  },
  text: {
    white: "#FFFFFF",
    inactive: "#C0C2C1",
    // The lightest grey that still carries words: hints in a field, and small print such as a count or a
    // date. 4.85:1 on white and 4.5:1 or more on the page and its pale tints, so it can be read; it was
    // #949695 (3:1), which is why it is only one step lighter than `label`.
    placeholder: "#707271",
    label: "#696A69",
    body: "#3D3E3D",
    header: "#121212",
    black: "#000",
  },
  accentColors: {
    errorLight: "#FFF5F5",
    errorStrong: "#B22323",
    errorDark: "#cc4e4e",
    errorHover: "#e08d8d",
    successLight: "#EBFFF2",
    successStrong: "#078236",
    warningLight: "#FFF9EB",
    warningStrong: "#946C0D",
    infoLight: "#EBF5FF",
    infoDark: "#0D5AA7",
    errorBackground: "#FFE5E5",
    tooltipBackground: "#FFF1C4",
    // for rating your understanding of a topic
    notSureLight: "#FAF5FF",
    notSureDark: "#6B21A8",
    okayLight: "#FFFBEB",
    okayDark: "#92400E",
    iKnowItLight: "#ECFEFF",
    iKnowItDark: "#155E75",
    saved: "#11C355",
    deepUnderstand: "#B4E5F4",
    progressing: "#FEE9BF",
    roomForImprovement: "#EADBF5",
    // Solid status fills that carry white text (see primaryAction.fill)
    errorFill: "#B22323",
    errorFillHover: "#cc4e4e",
    successFill: "#078236",
    // The outline of an empty field that will hold a right or a wrong answer (the new-question form).
    // Soft: three empty "incorrect answer" fields must not look like three errors.
    successBorderSoft: "#99E1AD",
    // The answer that turned out to be right, in a review. On the light theme a near-white card with a
    // green line; on a dark page it lights up, clearly brighter and greener than the question card above it
    // (the two were too close: the owner's note, 2026-10-02).
    correctAnswerFill: "#EDFFF2",
    correctAnswerBorder: "#99E1AD",
    // The answer the learner chose that turned out to be wrong, its partner
    wrongAnswerFill: "#FFF2F2",
    wrongAnswerBorder: "#E1ABAB",
    errorBorderSoft: "#E1ABAB",
    warningBorderSoft: "#E7CE8F",
    // The selected "i know it" rating, which carries a white label
    iKnowItFill: "#3B9BB9",
    // The selected "not sure" rating, which carries a white label
    notSureFill: "#8447B5",
  },
};

type JungleThemeColors = typeof lightThemeColorValues;

// "surface" + "borderQuestion" → "--jungle-surface-border-question"
function getThemeColorCssVariableName(colorGroupName: string, colorName: string): string {
  const toKebabCase = (camelCaseText: string) => camelCaseText.replace(/([a-z])([A-Z0-9])/g, "$1-$2").toLowerCase();
  return `--jungle-${toKebabCase(colorGroupName)}-${toKebabCase(colorName)}`;
}

function buildThemeAwareColors(lightValues: JungleThemeColors): JungleThemeColors {
  const themeAwareColors: Record<string, Record<string, string>> = {};
  for (const [colorGroupName, colorGroup] of Object.entries(lightValues)) {
    themeAwareColors[colorGroupName] = {};
    for (const [colorName, lightValue] of Object.entries(colorGroup)) {
      // The light value is the fallback, so a page that never loads the theme stylesheet (an isolated
      // preview, a test) looks exactly as it did before themes existed.
      themeAwareColors[colorGroupName][colorName] =
        `var(${getThemeColorCssVariableName(colorGroupName, colorName)}, ${lightValue})`;
    }
  }
  return themeAwareColors as JungleThemeColors;
}

const colors = buildThemeAwareColors(lightThemeColorValues);

/**
 * For a color that is close to, but not exactly, a theme color. It keeps its own light value, so the light
 * theme does not change by a pixel, and takes the theme color's value in the dark theme:
 *
 *   lightColorWithDarkTheme("#FAFAFA", colors.surface.background)
 *
 * It works because the light theme defines no variables (see buildThemeAwareColors): the first argument
 * simply replaces the fallback of the theme color's variable.
 */
function lightColorWithDarkTheme(lightColor: string, darkThemeColor: string): string {
  const cssVariableName = darkThemeColor.match(/^var\((--jungle-[a-z0-9-]+),/)?.[1];
  // Not a theme color (a mistake at the call site): the light color is the safe answer in both themes.
  if (!cssVariableName) return lightColor;
  return `var(${cssVariableName}, ${lightColor})`;
}

// Put this class on an element whose content must stay light in the dark theme because it sits on art
// with its own light surface (a paper letter, a page of the badge book, a white speech bubble). Inside
// it every theme color falls back to its light value. Defined in src/theme/jungleThemeColors.css.
const ALWAYS_LIGHT_THEME_CLASS_NAME = "jungle-always-light";

// The selector for a style that applies on a learner's dark page only, for the few cases a color cannot
// express (a shadow that must go, a filter, an opacity). Use it as a key in Chakra's `sx` or inside a
// pseudo prop: sx={{ [ON_JUNGLE_DARK_PAGE]: { boxShadow: "none" } }}. Unlike Chakra's `_dark`, it does not
// match the admin area's own dark mode. It starts with `html`, not `:root`: a selector that starts with a
// colon is attached to the styled element itself and matches nothing.
const ON_JUNGLE_DARK_PAGE = 'html[data-theme="dark"][data-jungle-dark] &';

// The selector for a style that applies on a learner's page in either theme, and not in the admin area or
// on a marketing page (src/theme/jungleColorTheme.ts sets data-jungle-app). For the styles of keyboard and
// field focus, which those two areas keep as they were built.
const ON_JUNGLE_APP_PAGE = "html[data-jungle-app] &";

// A text field on a learner's page takes a green line while it has focus (src/chakraTheme.ts). A field
// that should not (one that opens with focus already in it, where the green would be the first thing on
// the screen, or one whose line means something else, like "this field holds a link") spreads this onto
// its Input or Textarea and gets no shared line, in either theme: <Input {...KEEPS_OWN_FOCUS_LINE} />.
// What it shows on focus is then its own to say (onboarding's name field keeps the dark theme's green
// line, as before; the link field is green while it holds a link).
const KEEPS_OWN_FOCUS_LINE_ATTRIBUTE = "data-keeps-own-focus-line";
const KEEPS_OWN_FOCUS_LINE = { [KEEPS_OWN_FOCUS_LINE_ATTRIBUTE]: "" } as const;

// A raised pill (an answer to choose in onboarding) on a dark sheet. On the light theme it is a white pill
// on a white sheet, told apart by a soft grey shadow. A dark sheet has no room for that: the pill's fill at
// the sheet's own color cannot be told from the sheet, and a grey shadow is lighter than the sheet (the
// owner's notes, 2026-10-02). So there the pill is two steps lighter than the sheet with a lighter line
// around it and lighter again under the pointer. The chosen pill must not be mistaken for one under the
// pointer, so it has a green line twice as thick (the second pixel is a ring in its shadow, which takes no
// room) and brighter words. The shadow is a thin light edge along the top with a short soft shadow under
// it: black and white at a low alpha are shades of whatever the pill lies on, so one shadow serves both
// dark designs.
const RAISED_PILL_FILL = colors.surface.raisedPill;
const RAISED_PILL_FILL_UNDER_POINTER = colors.surface.raisedPillUnderPointer;
const RAISED_PILL_FILL_WHEN_CHOSEN = colors.surface.raisedPillChosen;
const RAISED_PILL_LINE = colors.surface.raisedPillLine;
const RAISED_PILL_LINE_WHEN_CHOSEN = colors.surface.raisedPillChosenLine;
const RAISED_PILL_LABEL = lightColorWithDarkTheme(lightThemeColorValues.secondaryAction.default, colors.text.body);
const RAISED_PILL_LABEL_WHEN_CHOSEN = lightColorWithDarkTheme(
  lightThemeColorValues.primaryAction.hover,
  colors.text.header
);
const RAISED_PILL_SHADOW_ON_DARK_PAGE =
  "inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 1px 2px rgba(0, 0, 0, 0.32), 0 4px 10px rgba(0, 0, 0, 0.16)";
const RAISED_PILL_SHADOW_WHEN_CHOSEN_ON_DARK_PAGE = `0 0 0 1px ${colors.surface.raisedPillChosenLine}, ${RAISED_PILL_SHADOW_ON_DARK_PAGE}`;

// The shadow under an answer card once the question is answered, on a dark page. The light theme tints
// that shadow green or red; on a dark page a light shadow shows as a pale band under the card.
const ANSWERED_CARD_SHADOW_ON_DARK_PAGE = "0 2px 3px rgba(0, 0, 0, 0.4)";

// The ring that turns while a whole page is waiting (signing in, opening checkout). A deep green on the light
// theme; a line on a dark page takes the lighter green, or it is 2:1 from the page.
const LOADING_RING_COLOR = lightColorWithDarkTheme("#15803D", colors.primaryAction.default);

export {
  colors,
  lightThemeColorValues,
  getThemeColorCssVariableName,
  lightColorWithDarkTheme,
  ALWAYS_LIGHT_THEME_CLASS_NAME,
  ON_JUNGLE_DARK_PAGE,
  ON_JUNGLE_APP_PAGE,
  KEEPS_OWN_FOCUS_LINE,
  KEEPS_OWN_FOCUS_LINE_ATTRIBUTE,
  ANSWERED_CARD_SHADOW_ON_DARK_PAGE,
  RAISED_PILL_FILL,
  RAISED_PILL_FILL_UNDER_POINTER,
  RAISED_PILL_FILL_WHEN_CHOSEN,
  RAISED_PILL_LINE,
  RAISED_PILL_LINE_WHEN_CHOSEN,
  RAISED_PILL_LABEL,
  RAISED_PILL_LABEL_WHEN_CHOSEN,
  RAISED_PILL_SHADOW_ON_DARK_PAGE,
  RAISED_PILL_SHADOW_WHEN_CHOSEN_ON_DARK_PAGE,
  LOADING_RING_COLOR,
};
